import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { getFcmServiceWorker } from './fcm-service-worker';

export interface SoundOption {
  id: string;
  label: string;
  path: string;
}

/** The five notification families a person can tune per device (per founder, 20 Oct 2026). */
export type SoundKind = 'chat' | 'tasks' | 'finance' | 'leads' | 'platform';

export interface SoundKindDef {
  id: SoundKind;
  label: string;
  desc: string;
  icon: string;
  gradient: string;
}

export interface PlayingSoundState {
  id: string;
  category: SoundKind;
}

export const SOUND_KINDS: SoundKindDef[] = [
  { id: 'chat', label: 'Chat', desc: 'Messages, mentions and replies', icon: 'chat_bubble', gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)' },
  { id: 'tasks', label: 'Tasks', desc: 'New tasks, reminders, more-time requests, done and accepted', icon: 'task_alt', gradient: 'linear-gradient(135deg, #0ea5e9, #06b6d4)' },
  { id: 'finance', label: 'Finance', desc: 'Approvals, payments, invoices and vendor requests', icon: 'payments', gradient: 'linear-gradient(135deg, #16a34a, #22c55e)' },
  { id: 'leads', label: 'Leads', desc: 'Lead assignments, follow-ups and status changes', icon: 'person_search', gradient: 'linear-gradient(135deg, #f59e0b, #f97316)' },
  { id: 'platform', label: 'Platform', desc: 'Inventory, workstation, project tracker, WhatsApp and the rest', icon: 'hub', gradient: 'linear-gradient(135deg, #64748b, #334155)' },
];

/** Which family a push or in-app notification belongs to, from the payload the server sends. */
export function soundKindOf(parsed: any): SoundKind {
  const type = String(parsed?.type || '');
  if (type === 'Tasks') return 'tasks';
  if (type.toLowerCase() === 'finance') return 'finance';
  if (type === 'Org-lead') return 'leads';
  if (
    type === 'Inventory' ||
    type === 'Workstation' ||
    type === 'Project-Tracker' ||
    type === 'Reimbursement' ||
    type === 'Whatsapp' ||
    parsed?.department === 'ADMIN'
  )
    return 'platform';
  return 'chat';
}

@Injectable({ providedIn: 'root' })
export class NotificationSoundService {
  readonly soundOptions: SoundOption[] = [
    { id: 'chime', label: 'Chime', path: '/assets/notification-sound-v2.mp3' },
    // { id: 'bell', label: 'Bell', path: '/assets/bell-v2.mp3' },
    { id: 'ping', label: 'Ping', path: '/assets/light_audio.mp3' },
    { id: 'alert', label: 'Alert', path: '/assets/leads/audio/notification-v2.mp3' },
    { id: 'pop', label: 'Pop', path: '/assets/leads/audio/studio.mp3' },
    { id: 'sound1', label: 'Sound 1', path: '/assets/leads/audio/sound1.mp3' },
    { id: 'sound2', label: 'Sound 2', path: '/assets/leads/audio/sound2.mp3' },
    { id: 'sound3', label: 'Sound 3', path: '/assets/leads/audio/sound3.mp3' },
  ];
  readonly kinds = SOUND_KINDS;

  /** A different chime per family, so the ear tells them apart. */
  private readonly defaultSoundId: Record<SoundKind, string> = {
    chat: 'chime',
    tasks: 'sound2',
    finance: 'ping',
    leads: 'alert',
    platform: 'pop',
  };

  private audio: Record<SoundKind, HTMLAudioElement> = {
    chat: new Audio(),
    tasks: new Audio(),
    finance: new Audio(),
    leads: new Audio(),
    platform: new Audio(),
  };
  private previewAudio = new Audio();

  private enabledSubject = new BehaviorSubject<Record<SoundKind, boolean>>(this.readAll('enabled', true));
  private mutedSubject = new BehaviorSubject<Record<SoundKind, boolean>>(this.readAll('muted', false));
  private soundSubject = new BehaviorSubject<Record<SoundKind, string>>(this.readAllSounds());
  private playingSoundSubject = new BehaviorSubject<PlayingSoundState | null>(null);

  enabled$ = this.enabledSubject.asObservable();
  muted$ = this.mutedSubject.asObservable();
  sound$ = this.soundSubject.asObservable();
  playingSound$ = this.playingSoundSubject.asObservable();

  constructor() {
    for (const k of Object.keys(this.audio) as SoundKind[]) {
      this.audio[k].preload = 'auto';
      this.audio[k].src = this.resolvePath(this.soundSubject.value[k]);
    }
    this.previewAudio.preload = 'auto';
    this.previewAudio.addEventListener('ended', () => this.playingSoundSubject.next(null));
    this.previewAudio.addEventListener('pause', () => this.playingSoundSubject.next(null));
    this.syncAllSettings();
  }

  /** The push service worker keeps a copy, so a family switched off here is not shown as a push either. */
  public syncAllSettings(): void {
    for (const k of this.kinds) {
      this.syncToServiceWorker(`enabled_${k.id}`, this.isEnabled(k.id));
      this.syncToServiceWorker(`muted_${k.id}`, this.isMuted(k.id));
      this.syncToServiceWorker(`sound_${k.id}`, this.soundIdOf(k.id));
    }
    // the old two keys, for a service worker that has not updated yet
    this.syncToServiceWorker('muted_chat', this.isMuted('chat'));
    this.syncToServiceWorker('muted_lead', this.isMuted('leads'));
    this.syncToServiceWorker('sound_chat', this.soundIdOf('chat'));
    this.syncToServiceWorker('sound_lead', this.soundIdOf('leads'));
  }

  private resolvePath(soundId: string): string {
    return this.soundOptions.find((o) => o.id === soundId)?.path ?? this.soundOptions[0].path;
  }

  private storageKey(kind: string, suffix: 'muted' | 'sound' | 'enabled'): string {
    const userId = localStorage.getItem('guestID');
    return userId ? `sound_${kind}_${suffix}_${userId}` : `sound_${kind}_${suffix}`;
  }

  /** Earlier builds had two families: chat and "lead" (everything else). Their settings seed the new ones. */
  private legacyKind(kind: SoundKind): string | null {
    return kind === 'leads' || kind === 'platform' ? 'lead' : null;
  }

  private readAll(suffix: 'muted' | 'enabled', fallback: boolean): Record<SoundKind, boolean> {
    const out = {} as Record<SoundKind, boolean>;
    for (const k of this.kinds) {
      let v = localStorage.getItem(this.storageKey(k.id, suffix));
      if (v === null && suffix === 'muted' && this.legacyKind(k.id)) v = localStorage.getItem(this.storageKey(this.legacyKind(k.id) as string, 'muted'));
      out[k.id] = v === null ? fallback : v === 'true';
    }
    return out;
  }

  private readAllSounds(): Record<SoundKind, string> {
    const out = {} as Record<SoundKind, string>;
    for (const k of this.kinds) {
      let v = localStorage.getItem(this.storageKey(k.id, 'sound'));
      if (!v && this.legacyKind(k.id)) v = localStorage.getItem(this.storageKey(this.legacyKind(k.id) as string, 'sound'));
      out[k.id] = v && this.soundOptions.some((o) => o.id === v) ? v : this.defaultSoundId[k.id];
    }
    return out;
  }

  // ── reads ──
  isEnabled(kind: SoundKind): boolean {
    return this.enabledSubject.value[kind] !== false;
  }
  isMuted(kind: SoundKind): boolean {
    return !!this.mutedSubject.value[kind];
  }
  soundIdOf(kind: SoundKind): string {
    return this.soundSubject.value[kind];
  }
  /** Kept for older callers. */
  get isChatMuted(): boolean {
    return this.isMuted('chat');
  }
  get isLeadMuted(): boolean {
    return this.isMuted('leads');
  }
  get chatSoundId(): string {
    return this.soundIdOf('chat');
  }
  get leadSoundId(): string {
    return this.soundIdOf('leads');
  }
  get playingSound(): PlayingSoundState | null {
    return this.playingSoundSubject.value;
  }
  isPlaying(soundId: string, category: SoundKind): boolean {
    const current = this.playingSoundSubject.value;
    return current !== null && current.id === soundId && current.category === category;
  }

  public syncToServiceWorker(key: string, value: any): void {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    // Not navigator.serviceWorker.controller — that is ngsw-worker.js. The FCM worker
    // owns a scope that excludes the page, so it never controls it and must be
    // addressed through its own registration.
    getFcmServiceWorker()
      .then((worker) => {
        worker?.postMessage({ type: 'UPDATE_SOUND_SETTINGS', key, value });
      })
      .catch(() => { });
  }

  // ── writes ──
  setEnabled(kind: SoundKind, on: boolean): void {
    localStorage.setItem(this.storageKey(kind, 'enabled'), String(on));
    this.enabledSubject.next({ ...this.enabledSubject.value, [kind]: on });
    this.syncToServiceWorker(`enabled_${kind}`, on);
  }
  setMuted(kind: SoundKind, muted: boolean): void {
    localStorage.setItem(this.storageKey(kind, 'muted'), String(muted));
    this.mutedSubject.next({ ...this.mutedSubject.value, [kind]: muted });
    this.syncToServiceWorker(`muted_${kind}`, muted);
    if (kind === 'chat') this.syncToServiceWorker('muted_chat', muted);
    if (kind === 'leads') this.syncToServiceWorker('muted_lead', muted);
  }
  setSound(kind: SoundKind, soundId: string): void {
    localStorage.setItem(this.storageKey(kind, 'sound'), soundId);
    this.soundSubject.next({ ...this.soundSubject.value, [kind]: soundId });
    this.audio[kind].src = this.resolvePath(soundId);
    this.syncToServiceWorker(`sound_${kind}`, soundId);
    if (kind === 'chat') this.syncToServiceWorker('sound_chat', soundId);
    if (kind === 'leads') this.syncToServiceWorker('sound_lead', soundId);
  }
  setChatMuted(muted: boolean): void {
    this.setMuted('chat', muted);
  }
  setLeadMuted(muted: boolean): void {
    this.setMuted('leads', muted);
  }
  setChatSound(soundId: string): void {
    this.setSound('chat', soundId);
  }
  setLeadSound(soundId: string): void {
    this.setSound('leads', soundId);
  }

  // ── play ──
  /** Plays the family's chime unless the family is off or silent on this device. */
  // ── Mute (30 Oct 2026): every notification paused until a time, set by NotificationMuteService ──
  private pausedUntil = 0;
  setPausedUntil(ms: number): void {
    this.pausedUntil = ms > Date.now() ? ms : 0;
  }
  /** All notifications are muted right now: no chime and no pop-up on this device. */
  isPaused(): boolean {
    return this.pausedUntil > Date.now();
  }
  play(kind: SoundKind): void {
    if (!this.isEnabled(kind) || this.isMuted(kind) || this.isPaused()) return;
    const a = this.audio[kind];
    a.currentTime = 0;
    a.play().catch((err) => console.warn('Notification sound play failed:', err));
  }
  /** For a push or in-app payload: its family's chime. */
  playFor(parsed: any): void {
    this.play(soundKindOf(parsed));
  }
  /** The service worker's message names a kind; old workers say 'lead' or 'chat'. */
  normalizeKind(kind: unknown, payload?: any): SoundKind {
    const k = String(kind || '');
    if (this.kinds.some((x) => x.id === k)) return k as SoundKind;
    if (payload) return soundKindOf(payload);
    return k === 'lead' ? 'leads' : 'chat';
  }
  playChat() {
    this.play('chat');
  }
  playLead() {
    this.play('leads');
  }
  previewChat() {
    this.previewSound(this.soundIdOf('chat'), 'chat');
  }
  previewLead() {
    this.previewSound(this.soundIdOf('leads'), 'leads');
  }

  previewSound(soundId: string, category: SoundKind = 'chat'): void {
    if (this.isPlaying(soundId, category)) {
      this.stopPreview();
      return;
    }
    this.previewAudio.pause();
    this.previewAudio.src = this.resolvePath(soundId);
    this.previewAudio.currentTime = 0;
    this.playingSoundSubject.next({ id: soundId, category });
    this.previewAudio.play().catch((err) => {
      console.warn('Sound preview failed:', err);
      this.playingSoundSubject.next(null);
    });
  }

  stopPreview(): void {
    this.previewAudio.pause();
    this.previewAudio.currentTime = 0;
    this.playingSoundSubject.next(null);
  }

  /** Browsers allow sound only after a gesture: a muted play-and-pause on each element unlocks them. */
  unlock() {
    for (const a of Object.values(this.audio)) {
      a.muted = true;
      a.play()
        .then(() => {
          a.pause();
          a.muted = false;
          a.currentTime = 0;
        })
        .catch(() => { });
    }
  }
}
